import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AdminProvider } from "@/contexts/AdminContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";

// Public Pages
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import DestinationsPage from "@/pages/DestinationsPage";
import PackagesPage from "@/pages/PackagesPage";
import PackageDetailPage from "@/pages/PackageDetailPage";
import ThemeToursPage from "@/pages/ThemeToursPage";
import ThemeTourDetailPage from "@/pages/ThemeTourDetailPage";
import CorporatePage from "@/pages/CorporatePage";
import CorporateProposalPage from "@/pages/CorporateProposalPage";
import ContactPage from "@/pages/ContactPage";
import GetQuotePage from "@/pages/GetQuotePage";
import WeddingzPage from "@/pages/WeddingzPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import CancellationPage from "@/pages/CancellationPage";
import B2BServicesPage from "@/pages/B2BServicesPage";
import VisaServicesPage from "@/pages/VisaServicesPage";
import FAQPage from "@/pages/FAQPage";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import PaymentPage from "@/pages/PaymentPage";
import SafarSamridhiPage from "@/pages/SafarSamridhiPage";
import SafarSamridhiBanner from "@/components/SafarSamridhiBanner";

// Admin Pages
import {
  AdminLayout,
  AdminLogin,
  AdminDashboard,
  AdminPackages,
  AdminThemeTours,
  AdminUsers,
  AdminActivityLogs,
  EnquiryReports,
  ContactReports,
  PackageReports,
  WeddingEnquiries,
  LeadsReports,
  CorporateReports,
  ThemeTourCategories,
  MetaAdsPage
} from "@/pages/admin";
import SitePages from "@/pages/admin/SitePages";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import AdminPromotions from "@/pages/admin/AdminPromotions";
import AdminFAQs from "@/pages/admin/AdminFAQs";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminBlog from "@/pages/admin/AdminBlog";
import SIPLeadsReport from "@/pages/admin/SIPLeadsReport";
import AdminLeadPipeline from "@/pages/admin/AdminLeadPipeline";

// Layout
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import CountdownBanner from "@/components/CountdownBanner";
import { useLocation } from "react-router-dom";

function PublicExtras() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return (
    <>
      <WhatsAppFloat />
      <SafarSamridhiBanner />
      <ExitIntentPopup />
    </>
  );
}

function PublicBanner() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return <CountdownBanner />;
}

function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <ScrollToTop />
        <AdminProvider>
          <PublicBanner />
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={
              <>
                <Header />
                <main className="flex-1"><HomePage /></main>
                <Footer />
              </>
            } />
            <Route path="/about" element={
              <>
                <Header />
                <main className="flex-1"><AboutPage /></main>
                <Footer />
              </>
            } />
            <Route path="/destinations" element={
              <>
                <Header />
                <main className="flex-1"><DestinationsPage /></main>
                <Footer />
              </>
            } />
            <Route path="/destinations/:category" element={
              <>
                <Header />
                <main className="flex-1"><DestinationsPage /></main>
                <Footer />
              </>
            } />
            <Route path="/packages" element={
              <>
                <Header />
                <main className="flex-1"><PackagesPage /></main>
                <Footer />
              </>
            } />
            <Route path="/packages/:id" element={
              <>
                <Header />
                <main className="flex-1"><PackageDetailPage /></main>
                <Footer />
              </>
            } />
            <Route path="/theme-tours" element={
              <>
                <Header />
                <main className="flex-1"><ThemeToursPage /></main>
                <Footer />
              </>
            } />
            <Route path="/theme-tours/:id" element={
              <>
                <Header />
                <main className="flex-1"><ThemeTourDetailPage /></main>
                <Footer />
              </>
            } />
            <Route path="/corporate" element={
              <>
                <Header />
                <main className="flex-1"><CorporatePage /></main>
                <Footer />
              </>
            } />
            <Route path="/corporate-proposal" element={
              <>
                <Header />
                <main className="flex-1"><CorporateProposalPage /></main>
                <Footer />
              </>
            } />
            <Route path="/weddingz" element={
              <>
                <Header />
                <main className="flex-1"><WeddingzPage /></main>
                <Footer />
              </>
            } />
            <Route path="/contact" element={
              <>
                <Header />
                <main className="flex-1"><ContactPage /></main>
                <Footer />
              </>
            } />
            <Route path="/get-quote" element={
              <>
                <Header />
                <main className="flex-1"><GetQuotePage /></main>
                <Footer />
              </>
            } />
            <Route path="/privacy" element={
              <>
                <Header />
                <main className="flex-1"><PrivacyPage /></main>
                <Footer />
              </>
            } />
            <Route path="/terms" element={
              <>
                <Header />
                <main className="flex-1"><TermsPage /></main>
                <Footer />
              </>
            } />
            <Route path="/cancellation" element={
              <>
                <Header />
                <main className="flex-1"><CancellationPage /></main>
                <Footer />
              </>
            } />
            <Route path="/faq" element={
              <>
                <Header />
                <main className="flex-1"><FAQPage /></main>
                <Footer />
              </>
            } />
            <Route path="/blog" element={
              <>
                <Header />
                <main className="flex-1"><BlogPage /></main>
                <Footer />
              </>
            } />
            <Route path="/blog/:slug" element={
              <>
                <Header />
                <main className="flex-1"><BlogPostPage /></main>
                <Footer />
              </>
            } />

            {/* Payment Route */}
            <Route path="/payment" element={
              <>
                <Header />
                <main className="flex-1"><PaymentPage /></main>
                <Footer />
              </>
            } />

            <Route path="/safar-samridhi" element={
              <>
                <Header />
                <main className="flex-1"><SafarSamridhiPage /></main>
                <Footer />
              </>
            } />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route index element={<AdminDashboard />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="packages" element={
                <ProtectedRoute requiredPermission="packages"><AdminPackages /></ProtectedRoute>
              } />
              <Route path="theme-tours" element={
                <ProtectedRoute requiredPermission="theme_tours"><AdminThemeTours /></ProtectedRoute>
              } />
              <Route path="theme-tour-categories" element={
                <ProtectedRoute requiredPermission="theme_tours"><ThemeTourCategories /></ProtectedRoute>
              } />
              <Route path="users" element={
                <ProtectedRoute requireSuperAdmin>
                  <AdminUsers />
                </ProtectedRoute>
              } />
              <Route path="activity-logs" element={
                <ProtectedRoute requireSuperAdmin>
                  <AdminActivityLogs />
                </ProtectedRoute>
              } />
              <Route path="reports/enquiries" element={
                <ProtectedRoute requiredPermission="reports"><EnquiryReports /></ProtectedRoute>
              } />
              <Route path="reports/contacts" element={
                <ProtectedRoute requiredPermission="reports"><ContactReports /></ProtectedRoute>
              } />
              <Route path="reports/packages" element={
                <ProtectedRoute requiredPermission="reports"><PackageReports /></ProtectedRoute>
              } />
              <Route path="reports/leads" element={
                <ProtectedRoute requiredPermission="reports"><LeadsReports /></ProtectedRoute>
              } />
              <Route path="reports/corporate" element={
                <ProtectedRoute requiredPermission="reports"><CorporateReports /></ProtectedRoute>
              } />
              <Route path="reports/sip-leads" element={
                <ProtectedRoute requiredPermission="reports"><SIPLeadsReport /></ProtectedRoute>
              } />
              <Route path="wedding-enquiries" element={
                <ProtectedRoute requiredPermission="leads"><WeddingEnquiries /></ProtectedRoute>
              } />
              <Route path="meta-ads" element={
                <ProtectedRoute requiredPermission="meta_ads"><MetaAdsPage /></ProtectedRoute>
              } />
              <Route path="site-pages" element={
                <ProtectedRoute requiredPermission="content"><SitePages /></ProtectedRoute>
              } />
              <Route path="security" element={
                <ProtectedRoute requiredPermission="security"><AdminSecurity /></ProtectedRoute>
              } />
              <Route path="promotions" element={
                <ProtectedRoute requiredPermission="promotions"><AdminPromotions /></ProtectedRoute>
              } />
              <Route path="faqs" element={
                <ProtectedRoute requiredPermission="content"><AdminFAQs /></ProtectedRoute>
              } />
              <Route path="reviews" element={
                <ProtectedRoute requiredPermission="content"><AdminReviews /></ProtectedRoute>
              } />
              <Route path="blog" element={
                <ProtectedRoute requiredPermission="content"><AdminBlog /></ProtectedRoute>
              } />
              <Route path="lead-pipeline" element={
                <ProtectedRoute requiredPermission="leads"><AdminLeadPipeline /></ProtectedRoute>
              } />
            </Route>

            {/* 404 Catch-all */}
            <Route path="*" element={
              <>
                <Header />
                <main className="flex-1 flex items-center justify-center min-h-[60vh]">
                  <div className="text-center px-4">
                    <h1 className="text-6xl font-bold text-slate-200 mb-4">404</h1>
                    <h2 className="text-xl font-semibold text-slate-700 mb-2">Page Not Found</h2>
                    <p className="text-slate-500 mb-6">The page you&apos;re looking for doesn&apos;t exist or has been moved.</p>
                    <a href="/" className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-2.5 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium" data-testid="go-home-btn">
                      Go Home
                    </a>
                  </div>
                </main>
                <Footer />
              </>
            } />
          </Routes>
        </AdminProvider>
        <PublicExtras />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
